<template>
  <VeeForm class="view-case" as="div" ref="form" :validation-schema="validationSchema">
    <div class="case-container">
      <div class="case-header" data-cy="case-header">
        <div class="case-header-section" data-cy="case-number">
          <h4>{{ $t('viewCase.case_number') }}</h4>
          <span>{{ warrantyOrder.caseNumber }}</span>
        </div>
        <div v-if="warrantyOrder && warrantyOrder.status" class="case-header-section" data-cy="case-status">
          <h4>{{ $t('viewCase.case_status_label') }}</h4>
          <span>{{ $t(`viewCase.case_status.${warrantyOrder?.status?.toLowerCase()}`) }}</span>
        </div>
        <div class="case-header-section" data-cy="case-creation-date">
          <h4 v-if="caseNotSubmitted">
            {{ $t('viewCase.case_creation_date') }}
          </h4>
          <h4 v-else>
            {{ $t('viewCase.case_submitted_date') }}
          </h4>
          <span>{{ formatDate(warrantyOrder.createdAt) }}</span>
        </div>
      </div>
      <div class="products-list" data-cy="product-list">
        <!-- ToDo move this to it's own component -->
        <div
          v-for="line in warrantyOrder.lines"
          :key="line.id"
          class="product"
          :data-cy="'product ' + line.productMainGroup"
          :class="[{ expanded: !line.collapsed }]">
          <div class="product-overview" v-on:click="toggleLine(line)">
            <div class="product-info">
              <div class="image" data-cy="product-image">
                <img :src="getBoxImageProxyUrl(line.productImageUrl)" alt="product-image" />
              </div>
              <div class="info">
                <h6 data-cy="product-name">
                  {{ line.productGroupName }}
                </h6>
                <p data-cy="item-size">
                  {{ line.productFabricSize }}
                </p>
                <p data-cy="item-number">
                  {{ line.productNumber }}
                </p>
              </div>
            </div>
            <div
              v-if="line.collapsed"
              class="customer-name"
              :class="{ alone: !(caseNotSubmitted && warrantyOrder.lines.length > 1) }"
              data-cy="customer-name-alone">
              <h6>{{ $t('viewCase.customer_name') }}</h6>
              <p>{{ line.customerName || '-' }}</p>
            </div>
            <div v-if="line.collapsed" class="defect-reason" data-cy="defect-reason-alone">
              <h6>{{ $t(`viewCase.defect_reason`) }}</h6>
              <p>{{ selectedDefectReasonDescription(line) }}</p>
            </div>
            <BackstageIcon
              v-if="caseNotSubmitted && warrantyOrder.lines.length > 1"
              class="delete-line"
              name="trash"
              data-cy="delete-line-button"
              v-on:click.self="deleteLine(line)" />
          </div>
          <div class="product-details" data-cy="product-details-grid">
            <div class="form-container">
              <div class="col">
                <BackstageInput
                  v-model="line.customerName"
                  data-cy-input="customer-name"
                  :disabled="!caseNotSubmitted"
                  :label="$t('viewCase.customer_name')"
                  type="text"
                  v-on:input="onChangeLine(line)">
                </BackstageInput>

                <!-- ToDo update the combobox to accept options as an object -->
                <BackstageCombobox
                  v-model="line.defectReason"
                  @click="onChangeLine(line)"
                  v-on:update:modelValue="setDefectType(line)"
                  :disabled="!caseNotSubmitted"
                  :name="`defect_reason:${line.id}`"
                  :label="getFieldTranslationByType(line, TranslationType.LABEL, CaseLineFields.DEFECT_REASON)"
                  :options="getDefectReasonsOptions(line.productMainGroup)"
                  data-cy="defect-reason"
                  options-data-cy="defect-reason-dropdown">
                </BackstageCombobox>

                <BackstageInputCalendar
                  :name="`purchase_date:${line.id}`"
                  v-model="line.purchaseDate"
                  v-on:update:modelValue="onChangeLine(line)"
                  class="date-selector"
                  :label="$t('viewCase.purchase_date')"
                  :disabled-dates="disabledDates()"
                  :placeholder="$t('global.datepicker_placeholder')"
                  :read-only="!caseNotSubmitted"
                  data-cy="purchase-date">
                </BackstageInputCalendar>
                <BackstageCombobox
                  v-if="line.productMainGroup === 'shoes'"
                  v-model="line.distanceRun"
                  v-on:update:modelValue="setWarrantyRunningDistancesType(line)"
                  @click="onChangeLine(line)"
                  :disabled="!caseNotSubmitted"
                  :name="`distance_run:${line.id}`"
                  :label="getFieldTranslationByType(line, TranslationType.LABEL, CaseLineFields.DISTANCE_RUN)"
                  :options="getDistanceRunOptions(line.productMainGroup)"
                  data-cy="distance-run"
                  options-data-cy="distance-run-dropdown">
                </BackstageCombobox>
                <p class="mandatory-fields">
                  {{ $t('viewCase.mandatory_fields') }}
                </p>
              </div>
              <div class="col">
                <!-- Note: Product code and Production code is same -->
                <BackstageCombobox
                  v-model="line.productCode"
                  v-on:update:modelValue="setProductionCodeType(line)"
                  @click="onChangeLine(line)"
                  :disabled="!caseNotSubmitted"
                  :name="`product_code:${line.id}`"
                  :options="getProductionCodeOptions(line.productMainGroup)"
                  :label="getFieldTranslationByType(line, TranslationType.LABEL, CaseLineFields.PRODUCT_CODE)"
                  :placeholder="
                    getFieldTranslationByType(line, TranslationType.PLACEHOLDER, CaseLineFields.PRODUCT_CODE)
                  "
                  data-cy="production-code"
                  options-data-cy="production-code-dropdown">
                  <template #tooltip>
                    <BackstageImageTooltip :image-location="getTooltipImageByProduct(line)" />
                  </template>
                </BackstageCombobox>

                <BackstageCombobox
                  v-model="line.factoryCode"
                  v-on:update:modelValue="setFactoryCodeType(line)"
                  @click="onChangeLine(line)"
                  :disabled="!caseNotSubmitted"
                  :name="`factory_code:${line.id}`"
                  :label="getFieldTranslationByType(line, TranslationType.LABEL, CaseLineFields.FACTORY_CODE)"
                  :options="getFactoryCodeOptions(line.productMainGroup)"
                  data-cy="factory-code"
                  options-data-cy="factory-code-dropdown">
                  <template #tooltip>
                    <BackstageImageTooltip :image-location="getTooltipImageByProduct(line)" />
                  </template>
                </BackstageCombobox>

                <div class="comment-group" data-cy="comments-field">
                  <BackstageTextarea
                    v-model="line.comments"
                    :name="`comments:${line.id}`"
                    :label="getTranslatedCommentLabel(line)"
                    :class="{ disabled: !caseNotSubmitted }"
                    :disabled="!caseNotSubmitted"
                    v-on:input="onChangeLine(line)">
                  </BackstageTextarea>
                </div>
              </div>
              <div class="col">
                <div v-if="line.images?.length || caseNotSubmitted" class="images-group" data-cy="image-upload">
                  <span class="images-label">
                    {{
                      $t(`viewCase.images.${line.productMainGroup?.toLowerCase()}.validation_label`, {
                        minImageCount: getMinImageCount(line.productMainGroup),
                      })
                    }}
                  </span>
                  <div class="image-upload-container">
                    <span v-if="caseNotSubmitted">
                      <!-- Large images row -->
                      <div class="large-images-row">
                        <CaseLineImageInput
                          v-for="[index, image] in requiredSlotImages(line)"
                          :id="`case-line-image-input-${line.id}-${index}`"
                          :key="index"
                          :disabled="!caseNotSubmitted"
                          :model="image"
                          :line="line"
                          v-on:click="() => setLineImageValidationError(line.id, true)"
                          :has-validation-error="shouldShowImageValidationError(line)"
                          :delete="() => deleteImage(line, image, index, ImageSlotType.Required)"
                          :change="(file: File) => onChangeImage(line, file, index, ImageSlotType.Required)">
                        </CaseLineImageInput>
                      </div>

                      <!-- Small images row -->
                      <div class="small-images-row">
                        <CaseLineImageInput
                          size="small"
                          v-for="[index, image] in optionalSlotImages(line)"
                          :id="`case-line-image-input-${line.id}-${index}`"
                          :key="index"
                          :disabled="!caseNotSubmitted"
                          :model="image"
                          :line="line"
                          v-on:click="setLineImageValidationError(line.id, true)"
                          :delete="() => deleteImage(line, image, index, ImageSlotType.Optional)"
                          :change="(file: File) => onChangeImage(line, file, index, ImageSlotType.Optional)">
                        </CaseLineImageInput>
                      </div>
                    </span>
                    <span v-else>
                      <div class="large-images-row">
                        <CaseLineImageInput
                          v-for="image in line.images"
                          :key="image.imageId"
                          :disabled="!caseNotSubmitted"
                          :image-metadata="{ isSubmittedImage: true }"
                          :model="image">
                        </CaseLineImageInput>
                      </div>
                    </span>
                  </div>
                  <span v-if="shouldShowImageValidationError(line)" class="validation-error">
                    {{
                      $t('viewCase.image_validation_error_message', {
                        minImageCount: getMinImageCount(line.productMainGroup),
                      })
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </VeeForm>
</template>

<script lang="ts">
import { storeToRefs } from 'pinia';
import { Form as VeeForm } from 'vee-validate';
import { defineComponent, type PropType } from 'vue';
import { object, type ObjectShape } from 'yup';

import { CaseImage } from '@/models/case-image.model';
import { CaseLineFields } from '@/models/case-line-fields.enum';
import { CaseLine } from '@/models/case-line.model';
import { Case } from '@/models/case.model';
import { DefectReason } from '@/models/defect-reason.model';
import { FactoryCode } from '@/models/factory-code.model';
import type { ProductionCode } from '@/models/production-code.model';
import {
  CaseImageModel,
  CaseImageType,
  CaseStatus,
  CaseType,
  LegalEntityEnum,
  ProductMainGroups,
  ValidatorResult,
} from '@onrunning/backstage-models';

import { CasesService } from '@/services/cases.service';

import { useAddEditCaseStore } from '@/stores/add-edit-case';
import { useUserStore } from '@/stores/user';

import CaseLineImageInput from '@/components/cases/case-line-image-input.vue';
import BackstageCombobox from '@/components/common/BackstageCombobox.vue';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BackstageImageTooltip from '@/components/common/BackstageImageTooltip.vue';
import BackstageInput from '@/components/common/BackstageInput.vue';
import BackstageInputCalendar from '@/components/common/BackstageInputCalendar.vue';
import BackstageTextarea from '@/components/common/BackstageTextarea.vue';

import { useBPLogger } from '@/composables/business-process-logger';
import { useDocumentHelpers } from '@/composables/document-helpers';
import { useFormValidation } from '@/composables/form-validation';
import { getBoxImageProxyUrl, sortAlphabetically } from '@/composables/utils';
import { useFormat } from '../composables/format';

import { accountMixins } from '@/mixins/account.mixins';
import { mappersMixins } from '@/mixins/mappers.mixins';

import { useImageHelper } from '@/composables/useImageHelper';
import { useNewRelic } from '@/composables/useNewRelic';
import { imageSlotConfig, productImageSlots } from '@/constants/case.constants';
import type { WarrantyRunningDistancesModel } from '@/models/warranty-running-distances.model';
import { ImageSlotType } from '@/types/case.enum';
import { type CaseLineSlotImage, type CaseLineSlots } from '@/types/case.interface';
import { isDateAfter, today } from '@/utils/temporalUtils';
import apparelProductionCodeImage from '../assets/img/apparel-accessory-production-code.png';
import shoeProductionCodeImage from '../assets/img/shoe-production-code.png';

const MAX_IMAGES_TO_BE_UPLOADED = 6;
const WARRANTY_IMAGES_REQUIRED_LEGAL_ENTITIES: string[] = [
  LegalEntityEnum.ON_AG,
  LegalEntityEnum.ON_CH,
  LegalEntityEnum.ON_UK,
];

export enum TranslationType {
  PLACEHOLDER = 'placeholder',
  LABEL = 'label',
}

export default defineComponent({
  name: 'view-case',
  components: {
    CaseLineImageInput,
    BackstageTextarea,
    BackstageInput,
    BackstageInputCalendar,
    BackstageIcon,
    BackstageCombobox,
    BackstageImageTooltip,
    VeeForm,
  },
  emits: {
    'emit-valid-case': (payload: { isValidCase: boolean; caseOrder: Case }) => payload,
    'delete-line': (productId: string) => productId,
  },
  props: {
    id: {
      type: String,
      default: undefined,
    },
    caseOrder: {
      type: Object as PropType<Case>,
      default: new Case(),
    },
  },
  setup() {
    const { selectedAccount, selectedAccountId } = storeToRefs(useUserStore());
    const { addEditingCases } = useAddEditCaseStore();
    const { editingCases } = storeToRefs(useAddEditCaseStore());
    const { reEncodeImage } = useImageHelper();
    const { dateRequiredValidation, textRequiredValidation } = useFormValidation();
    const { getTranslationFromTranslateField, formatDate } = useFormat();
    const { pageViewBPLog } = useBPLogger();
    const { generateFileSHA256 } = useDocumentHelpers();
    const { noticeError } = useNewRelic();

    return {
      ImageSlotType,
      editingCases,
      CaseLineFields,
      TranslationType,
      selectedAccount,
      selectedAccountId,
      shoeProductionCodeImage,
      apparelProductionCodeImage,
      formatDate,
      generateFileSHA256,
      addEditingCases,
      sortAlphabetically,
      getBoxImageProxyUrl,
      dateRequiredValidation,
      textRequiredValidation,
      getTranslationFromTranslateField,
      pageViewBPLog,
      reEncodeImage,
      noticeError,
    };
  },
  data() {
    return {
      savingQueue: [] as (() => Promise<any>)[],
      maxImageCount: MAX_IMAGES_TO_BE_UPLOADED,
      isValidCase: false,
      saving: false,
      loading: true,
      casesService: new CasesService(),
      warrantyOrder: new Case(),
      defectReasons: new Array<DefectReason>(),
      factoryCodesMap: new Map<ProductMainGroups, FactoryCode[]>(),
      productionCodesMap: new Map<ProductMainGroups, ProductionCode[]>(),
      warrantyRunningDistances: new Map<ProductMainGroups, WarrantyRunningDistancesModel[]>(),
      productMainGroups: ProductMainGroups,
      purchaseDate: undefined as unknown as Date,
      validationSchema: {},
      linesImageHashes: new Map<string, Map<string, string>>(),
      linesImageMap: new Map<string, CaseLineSlots>(),
      lineImageValidationErrors: new Map<string, boolean>(),
    };
  },
  computed: {
    formattedDefectReasons() {
      return this.defectReasons.map((defectReason) => ({
        ...defectReason,
        description: this.getTranslationFromTranslateField(defectReason.description),
      }));
    },
    isWarranty(): boolean {
      return this.warrantyOrder.type === CaseType.Warranty;
    },
    isReturn(): boolean {
      return this.warrantyOrder.type === CaseType.Return;
    },
    warrantyImagesRequired(): boolean {
      return this.isWarranty && WARRANTY_IMAGES_REQUIRED_LEGAL_ENTITIES.includes(this.selectedAccount?.legalEntity);
    },
    returnColumnDefinitions(): any[] {
      return [
        { headerValueGetter: () => this.$t('viewCase.item_number'), field: 'productNumber' },
        { headerValueGetter: () => this.$t('viewCase.item_product_group'), field: 'productGroupName' },
        { headerValueGetter: () => this.$t('viewCase.item_quantity'), field: 'quantity' },
      ];
    },
    caseNotSubmitted(): boolean {
      return this.warrantyOrder.status === CaseStatus.NotSubmitted;
    },

    requiredSlotImages() {
      return (line: CaseLine): any => {
        return this.linesImageMap.get(line.id)?.required;
      };
    },

    optionalSlotImages() {
      return (line: CaseLine): any => {
        return this.linesImageMap.get(line.id)?.optional;
      };
    },
  },
  watch: {
    warrantyOrder: {
      deep: true,
      handler() {
        this.emitValidCase();
      },
    },
  },
  async mounted() {
    this.warrantyOrder = this.caseOrder;
    await Promise.all([
      this.fetchDefectReasons(this.warrantyOrder.type),
      this.fetchFactoryCodes(),
      this.fetchProductionCodes(),
      this.fetchWarrantyRunningDistances(),
    ]);
    this.buildValidationSchema();
    await this.setLineImageData();

    this.pageViewBPLog(true, { page_name: 'view-warranty-case', case_number: this.warrantyOrder?.caseNumber });
  },
  methods: {
    ...accountMixins,
    ...mappersMixins,
    disabledDates() {
      return (target: Date) => {
        const now = today();

        return isDateAfter(target, now);
      };
    },

    async emitValidCase(): Promise<void> {
      // Manual validation for warranty cases
      let areImagesProvided = this.warrantyImagesRequired
        ? this.warrantyOrder.lines.every((line: CaseLine) => this.allImagesAreUploaded(line))
        : this.warrantyOrder.lines.every((line: CaseLine) => this.allImagesOrCommentProvided(line));
      const areParchedDatesProvided = this.warrantyOrder.lines.every((line: CaseLine) => line.purchaseDate);

      let isRequiredFieldsFilled;
      try {
        isRequiredFieldsFilled = await (this.$refs as any).form?.validate({ silent: true });
      } catch (error) {
        console.error('Error validating form:', error);
      }

      let manualValidationResults = areImagesProvided && areParchedDatesProvided;

      this.$emit('emit-valid-case', {
        isValidCase: isRequiredFieldsFilled.valid && manualValidationResults,
        caseOrder: this.warrantyOrder,
      });
    },

    getTooltipImageByProduct(line: CaseLine) {
      return this.isShoe(line) ? shoeProductionCodeImage : apparelProductionCodeImage;
    },

    isShoe(line: CaseLine) {
      return line.productMainGroup === ProductMainGroups.shoes;
    },
    allImagesAreUploaded(line: CaseLine): boolean {
      const imagesConfig = productImageSlots.get(line.productMainGroup);

      if (!imagesConfig) {
        return false;
      }

      const requiredImageTypes = Array.from(imagesConfig.values());
      const minImageCount = this.getMinImageCount(line.productMainGroup);

      return (
        line.images !== undefined &&
        line.images.length >= minImageCount &&
        this.containsAllTypes(line.images, requiredImageTypes)
      );
    },

    containsAllTypes(images: CaseImageModel[] | undefined, types: CaseImageType[]): boolean {
      if (!images) return false;
      const imageTypes = new Set(images.map((img) => img.imageType));

      return types.every((type) => imageTypes.has(type));
    },

    allImagesOrCommentProvided(line: CaseLine): boolean {
      return this.allImagesAreUploaded(line) || !!line.comments;
    },
    hideImagesErrorMessage(line: CaseLine): boolean {
      return this.warrantyImagesRequired ? this.allImagesAreUploaded(line) : true;
    },
    productCodePlaceholder(line: CaseLine) {
      return this.$t(`viewCase.product_code_placeholder_${line.productMainGroup?.toLowerCase()}`).toString();
    },

    getFieldTranslationByType(line: CaseLine, type: TranslationType, field: CaseLineFields) {
      return this.$t(`viewCase.${field}_${type}_${line.productMainGroup?.toLowerCase()}`).toString();
    },

    additionalDetailsRequired(line: CaseLine): boolean {
      // If image uploaded for warranty case, additional details are not required
      const lineImageMap = this.linesImageMap.get(line.id);

      if (!lineImageMap) {
        return true;
      }

      return this.isWarranty && !this.allImagesAreUploaded(line);
    },

    getTranslatedCommentLabel(line: CaseLine) {
      return this.additionalDetailsRequired(line)
        ? this.$t('viewCase.comments')
        : this.$t('viewCase.comments_optional');
    },

    selectedDefectReasonDescription(line: CaseLine) {
      const selectedDefectReason = this.defectReasons.find(
        (defectReason: DefectReason) =>
          defectReason.productMainGroups.includes(line.productMainGroup) && defectReason.type === line.defectReason,
      );

      return selectedDefectReason ? this.getTranslationFromTranslateField(selectedDefectReason.description) : '-';
    },

    buildValidationSchema() {
      let schema: ObjectShape = {};
      this.warrantyOrder.lines.forEach((line) => {
        const lineId = line.id;
        schema[`defect_reason:${lineId}`] = this.textRequiredValidation(`defect_reason:${lineId}`);
        schema[`purchase_date:${lineId}`] = this.dateRequiredValidation(`purchase_date:${lineId}`);
        schema[`product_code:${lineId}`] = this.textRequiredValidation(`product_code:${lineId}`);
        schema[`factory_code:${lineId}`] = this.textRequiredValidation(`factory_code:${lineId}`);

        if (this.additionalDetailsRequired(line)) {
          schema[`comments:${lineId}`] = this.textRequiredValidation(`comments:${lineId}`);
        }

        if (line.productMainGroup === ProductMainGroups.shoes) {
          schema[`distance_run:${lineId}`] = this.textRequiredValidation(`distance_run:${lineId}`);
        }
      });

      this.validationSchema = object(schema);
    },

    /**
     * @returns {Promise<void>}
     * @description Fetches previously uploaded images from AWS, recalculates their hashes,
     * and stores them in the linesImageHashes map.
     * It initializes image slots, and assigns images to their respective positions in the linesImageMap.
     */
    async setLineImageData(): Promise<void> {
      if (!this.warrantyOrder?.lines) {
        return;
      }

      for (const line of this.warrantyOrder.lines) {
        const lineImageHashes = new Map<string, string>();
        const slots = this.initializeLineImagesMap(line);

        for (const image of line.images || []) {
          try {
            const imgSHA256 = await this.generateFileSHA256(image.imageUrl);
            lineImageHashes.set(image.imageId, imgSHA256);
          } catch (error: any) {
            this.noticeError(error, {
              message: 'Error generating file hash for case line image',
              details: {
                caseId: this.warrantyOrder.id,
                lineId: line.id,
                imageId: image.imageId,
              },
            });
          }

          this.setLineImageMap(line, image, slots);
        }

        this.linesImageHashes.set(line.id, lineImageHashes);
        this.linesImageMap.set(line.id, slots);
      }
    },

    initializeLineImageMap(line: CaseLine): Map<number, CaseImage> {
      const lineImageMap = new Map<number, CaseImage>();
      const imagesConfig = productImageSlots.get(line.productMainGroup);

      if (!imagesConfig) {
        return lineImageMap;
      }

      for (const [position, imageType] of imagesConfig.entries()) {
        lineImageMap.set(position, { imageType: imageType } as CaseImage);
      }

      return lineImageMap;
    },

    initializeLineImagesMap(line: CaseLine): CaseLineSlots {
      const requiredCount = this.getMinImageCount(line.productMainGroup);
      const slotConfig = productImageSlots.get(line.productMainGroup) || new Map();

      const required = new Map<number, CaseLineSlotImage>();
      const optional = new Map<number, CaseLineSlotImage>();

      for (let i = 0; i < this.maxImageCount; i++) {
        const index = i + 1;
        const imageType = slotConfig.get(index) as CaseImageType;
        const isRequired = i < requiredCount;

        const meta: CaseLineSlotImage = {
          imageType,
          required: isRequired,
          backgroundImage: imageSlotConfig[imageType]?.background,
          label: isRequired ? this.$t(`viewCase.images.${imageSlotConfig[imageType]?.label}_label`) : undefined,
        };

        (isRequired ? required : optional).set(index, meta);
      }

      return { required, optional };
    },

    setLineImageMap(line: CaseLine, image: CaseImageModel, slots: CaseLineSlots) {
      const requiredCount = this.getMinImageCount(line.productMainGroup);
      const slot = image.imageType ? imageSlotConfig[image.imageType]?.position : undefined;

      if (slot) {
        const lineImage = slots[ImageSlotType.Required].get(slot) as CaseLineSlotImage;
        slots[ImageSlotType.Required].set(slot, {
          ...lineImage,
          ...{ imageUrl: image.imageUrl, imageId: image.imageId },
        });
      } else {
        for (let i = requiredCount + 1; i <= MAX_IMAGES_TO_BE_UPLOADED; i++) {
          const lineImage = slots[ImageSlotType.Optional].get(i) as CaseLineSlotImage;

          if (!lineImage.imageUrl) {
            slots[ImageSlotType.Optional].set(i, {
              ...lineImage,
              ...{ imageUrl: image.imageUrl, imageId: image.imageId },
            });
            break;
          }
        }
      }
    },

    goBackToCases() {
      this.$router.push(`/${(this.warrantyOrder.type as CaseType).toLowerCase()}-cases`);
    },

    async fetchDefectReasons(caseType: CaseType) {
      const validationResult = await this.casesService.getDefectReasons(caseType, this.selectedAccount?.billingCountry);

      if (validationResult.isValid()) {
        this.defectReasons = validationResult.model as DefectReason[];
      }
    },

    categorizeFactoryCodesByMainGroups(factoryCodes: FactoryCode[]) {
      const categorizedFactoryCodes = new Map<ProductMainGroups, FactoryCode[]>();

      factoryCodes.forEach((factoryCode) => {
        factoryCode.productMainGroups.forEach((group) => {
          categorizedFactoryCodes.set(group, (categorizedFactoryCodes.get(group) || []).concat([factoryCode]));
        });
      });

      return categorizedFactoryCodes;
    },

    categorizeProductionCodesByMainGroups(productionCodes: ProductionCode[]) {
      const categorizedProductionCodes = new Map<ProductMainGroups, ProductionCode[]>();

      productionCodes.forEach((productionCode) => {
        productionCode.productMainGroups.forEach((group) => {
          categorizedProductionCodes.set(group, (categorizedProductionCodes.get(group) || []).concat([productionCode]));
        });
      });

      return categorizedProductionCodes;
    },

    categorizeWarrantyRunningDistancesByMainGroups(warrantyRunningDistances: WarrantyRunningDistancesModel[]) {
      const categorizedWarrantyRunningDistances = warrantyRunningDistances.reduce((map, warrantyRunningDistance) => {
        warrantyRunningDistance.productMainGroups.forEach((group) => {
          const currentGroup = map.get(group) || [];
          currentGroup.push(warrantyRunningDistance);
          map.set(group, currentGroup);
        });

        return map;
      }, new Map<ProductMainGroups, WarrantyRunningDistancesModel[]>());

      return categorizedWarrantyRunningDistances;
    },

    async fetchFactoryCodes() {
      const validationResult = await this.casesService.getFactoryCodes();
      if (validationResult.isValid()) {
        this.factoryCodesMap = this.categorizeFactoryCodesByMainGroups(validationResult.model as FactoryCode[]);
      }
    },

    async fetchProductionCodes() {
      const validationResult = await this.casesService.getProductionCodes();

      if (validationResult.isValid()) {
        this.productionCodesMap = this.categorizeProductionCodesByMainGroups(
          validationResult.model as ProductionCode[],
        );
      }
    },

    async fetchWarrantyRunningDistances() {
      const validationResult = await this.casesService.getWarrantyRunningDistances();

      if (validationResult.isValid()) {
        this.warrantyRunningDistances = this.categorizeWarrantyRunningDistancesByMainGroups(
          validationResult.model as WarrantyRunningDistancesModel[],
        );
      }
    },

    deleteImage(line: CaseLine, image: CaseImage, index: number, type: ImageSlotType) {
      return this.queueUpdate(async () => {
        const response = await this.casesService.deleteLineImage(
          this.id || this.warrantyOrder.id,
          line,
          image.imageId,
          this.selectedAccountId,
        );

        if (response.isValid()) {
          this.linesImageHashes.get(line.id)?.delete(image.imageId);

          const lineImages = this.linesImageMap.get(line.id)?.[type] as Map<number, CaseLineSlotImage>;
          const lineImage = lineImages?.get(index) as CaseLineSlotImage;

          lineImages.set(index, {
            ...lineImage,
            ...{ imageUrl: undefined, imageId: undefined },
          });

          line.images = line.images?.filter((obj: CaseImage) => obj.imageId !== image.imageId);
          this.saveLineChangesToStore(line);
          this.buildValidationSchema();
        }
      });
    },

    async onChangeImage(line: CaseLine, imageFile: File, index: number, type: ImageSlotType) {
      let safeImage: File;

      try {
        safeImage = await this.reEncodeImage(imageFile);
      } catch (error: any) {
        this.noticeError(error, {
          message: 'Error re-encoding image for case line',
          details: {
            caseId: this.caseOrder.id,
            lineId: line.id,
            imageIndex: index,
          },
        });

        this.$snotify.error(this.$t('viewCase.image_upload_error'));

        return;
      }

      const imgSHA256 = (await this.generateFileSHA256(safeImage)) as string;

      try {
        this.checkImageSimilarity(imgSHA256, line);
      } catch (error) {
        this.$snotify.error(this.$t('viewCase.similar_image_upload'));

        return;
      }

      const imageType = productImageSlots.get(line.productMainGroup)?.get(index);

      return this.queueUpdate(async () => {
        try {
          const response = await this.casesService.addLineImage(
            this.id || this.caseOrder.id,
            line,
            safeImage,
            this.selectedAccountId,
            imageType,
          );

          if (response.isValid()) {
            const image = response.model as CaseImage;

            const lineImageHashes = this.linesImageHashes.get(line.id) || new Map<string, string>();
            lineImageHashes.set(image.imageId, imgSHA256);
            this.linesImageHashes.set(line.id, lineImageHashes);

            line?.images?.push(image);

            const lineImages = this.linesImageMap.get(line.id)?.[type] as Map<number, CaseLineSlotImage>;
            const lineImage = lineImages?.get(index) as CaseLineSlotImage;

            lineImages.set(index, {
              ...lineImage,
              ...{ imageUrl: image.imageUrl, imageId: image.imageId },
            });

            this.saveLineChangesToStore(line);
            this.buildValidationSchema();

            if (this.allImagesAreUploaded(line)) {
              this.setLineImageValidationError(line.id, false);
            }
          }
        } catch (error: any) {
          this.noticeError(error, {
            message: 'Error generating file hash for case line image',
            details: {
              caseId: this.caseOrder.id,
              lineId: line.id,
              imageIndex: index,
            },
          });

          this.$snotify.error(this.$t('viewCase.image_upload_error'));
        }
      });
    },
    checkImageSimilarity(imgSHA256: string, line: CaseLine): void {
      const lineImageHashes = this.linesImageHashes.get(line.id) || new Map<string, string>();
      const isTooSimilar = [...lineImageHashes.values()].some((existingSHA256) => existingSHA256 === imgSHA256);

      if (isTooSimilar) {
        throw new Error(this.$t('viewCases.similar_image_uploaded'));
      }
    },
    saveLineChangesToStore(updatedLine: CaseLine) {
      const lineIndex = this.warrantyOrder?.lines.findIndex(
        (warrantyOrderLine: CaseLine) => warrantyOrderLine.id === updatedLine.id,
      );

      this.warrantyOrder?.lines.splice(lineIndex, 1, updatedLine);
      this.addEditingCases([this.warrantyOrder]);
      this.warrantyOrder = this.editingCases[0];
    },
    onChangeLine(line: CaseLine) {
      this.queueUpdate(async () => {
        await this.casesService.updateLine(this.id || this.warrantyOrder.id, line, this.selectedAccountId);
      });
      this.saveLineChangesToStore(line);
    },
    async deleteLine(line: CaseLine) {
      this.loading = true;
      const response = await this.casesService.deleteLine(
        this.id || this.warrantyOrder.id,
        line,
        this.selectedAccountId,
      );

      if (response.isValid()) {
        this.warrantyOrder.lines = this.warrantyOrder.lines.filter((obj: CaseLine) => obj !== line);
      }

      this.loading = false;
      this.buildValidationSchema();
      this.emitValidCase();
      this.$emit('delete-line', line.productId);
    },
    toggleLine(line: CaseLine) {
      line.collapsed = !line.collapsed;
    },
    onChangeCase() {
      this.queueUpdate(async () => this.updateCase());
    },
    async updateCase() {
      await this.casesService.update(
        this.warrantyOrder.id,
        this.mapCaseUpdate(this.warrantyOrder, false),
        this.selectedAccountId,
      );

      this.addEditingCases([this.warrantyOrder]);
    },
    async queueUpdate(updateCodeBlock?: () => Promise<ValidatorResult<Case | CaseLine> | void>) {
      // if we are already saving we queue the update
      if (this.saving) {
        if (updateCodeBlock) {
          this.savingQueue.unshift(updateCodeBlock);
        }

        return;
      }

      this.saving = true;

      // if no code block we try to pop last queued code block
      if (!updateCodeBlock) {
        updateCodeBlock = this.savingQueue.pop();
      }

      try {
        if (updateCodeBlock) {
          await updateCodeBlock();
        } else {
          console.error('error trying to pop a queed save case task');
        }
      } catch (err) {
        console.error(err);
      }

      this.saving = false;

      if (this.savingQueue.length > 0) {
        this.queueUpdate();
      }
    },

    ensureNALast(options: string[]) {
      const naIndex = options.indexOf('N/A');

      if (naIndex !== -1) {
        options.splice(naIndex, 1);
        options.push('N/A');
      }

      return options;
    },

    getProductionCodeOptions(productMainGroup: ProductMainGroups) {
      const productCodesByMainGroup = this.productionCodesMap.get(productMainGroup) ?? [];
      const productCodes = productCodesByMainGroup.map((productCode) => {
        return productCode.type;
      });

      return this.ensureNALast(sortAlphabetically(productCodes));
    },

    getFactoryCodeOptions(productMainGroup: ProductMainGroups) {
      const factoryCodesByMainGroup = this.factoryCodesMap.get(productMainGroup) ?? [];
      const factoryCodes = factoryCodesByMainGroup.map((factoryCode) => {
        return factoryCode.type;
      });

      return this.ensureNALast(sortAlphabetically(factoryCodes));
    },

    getDistanceRunOptions(productMainGroup: ProductMainGroups): string[] {
      const runningDistancesByMainGroup = this.warrantyRunningDistances.get(productMainGroup) ?? [];
      runningDistancesByMainGroup.sort((a, b) => a.sortingOrder - b.sortingOrder);
      const runningDistances = runningDistancesByMainGroup.map((runningDistance) => {
        return this.getTranslationFromTranslateField(runningDistance.description);
      });

      return this.ensureNALast(runningDistances);
    },

    getDefectReasonsOptions(productMainGroup: ProductMainGroups) {
      const filteredDefectedReasons = this.defectReasons.filter((defectReason: DefectReason) =>
        defectReason.productMainGroups.includes(productMainGroup),
      );
      const filteredDefectedReasonTypes = filteredDefectedReasons.map((reasons) => {
        return this.getTranslationFromTranslateField(reasons.description);
      });

      return this.ensureNALast(sortAlphabetically(filteredDefectedReasonTypes));
    },

    setProductionCodeType(line: CaseLine): void {
      const productionCodes = this.productionCodesMap.get(line.productMainGroup) || [];

      productionCodes.forEach((productionCode: FactoryCode) => {
        if (Object.values(productionCode.description).includes(line.productCode)) {
          line.productCode = productionCode.type;
        }
      });
    },

    setFactoryCodeType(line: CaseLine): void {
      const factoryCodes = this.factoryCodesMap.get(line.productMainGroup) || [];

      factoryCodes.forEach((factoryCode: FactoryCode) => {
        if (Object.values(factoryCode.description).includes(line.factoryCode)) {
          line.factoryCode = factoryCode.type;
        }
      });
    },

    setDefectType(line: CaseLine) {
      const defectReason = this.defectReasons.find(
        (defectReason: DefectReason) =>
          defectReason.productMainGroups.includes(line.productMainGroup) &&
          Object.values(defectReason.description).includes(line.defectReason),
      );

      if (defectReason) {
        line.defectReason = defectReason.type;
      }
    },

    setWarrantyRunningDistancesType(line: CaseLine): void {
      const runningDistances = this.warrantyRunningDistances.get(line.productMainGroup) || [];

      runningDistances.forEach((runningDistance: WarrantyRunningDistancesModel) => {
        if (Object.values(runningDistance.description).includes(line.distanceRun)) {
          line.distanceRun = runningDistance.type;
        }
      });
    },

    getMinImageCount(productMainGroup: ProductMainGroups): number {
      return productImageSlots.get(productMainGroup)?.size || 0;
    },

    getTranslatedImageLabel(imageType: CaseImageType): string | undefined {
      return imageSlotConfig[imageType].label
        ? this.$t(`viewCase.images.${imageSlotConfig[imageType].label}_label`)
        : undefined;
    },

    setLineImageValidationError(lineId: string, isError: boolean) {
      this.lineImageValidationErrors.set(lineId, isError);
    },

    shouldShowImageValidationError(line: CaseLine): boolean {
      return !this.hideImagesErrorMessage(line) && !!this.lineImageValidationErrors.get(line.id);
    },
  },
});
</script>

<style scoped lang="scss">
.view-case {
  @include common-container;
  max-width: 1660px;
  padding: 0 20px;
  margin-top: 1.5rem;

  .case-container {
    background-color: var(--bg-page-color);

    .case-header {
      display: flex;
      align-items: center;
      background-color: var(--primary-bg-color);
      border-radius: 0.25rem 0.25rem 0 0;
      padding: 1rem 1.25rem;
      gap: 16px;

      .case-header-section {
        width: 100%;
        text-align: left;

        h4 {
          line-height: 150%;
          margin: 0 0 0.25rem;
          font-weight: 700;
          font-size: 1rem;
        }

        span {
          line-height: 150%;
          color: var(--primary-text-remark-color);
          font-weight: 400;
          font-size: 0.875rem;
        }
      }
    }

    .products-list {
      background-color: var(--bg-page-color);
      border-radius: 0.25rem 0.25rem 0 0;
      overflow: visible;

      .product {
        padding: 0.75rem 1.25rem;
        background-color: var(--primary-bg-color);
        margin-bottom: 0.0625rem;

        .product-overview {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          cursor: pointer;

          .customer-name {
            width: 34.3%;
            padding-left: 20px;
          }

          .defect-reason {
            padding-left: 20px;
            width: 29%;
          }

          .product-info {
            display: flex;
            align-items: center;
            gap: 1.25rem;
            width: 33%;

            .image {
              width: 5rem;
              height: 5rem;

              img {
                height: 100%;
                width: 100%;
                object-fit: contain;
              }
            }
          }

          h6 {
            text-align: left;
            color: var(--primary-text-color);
            font-size: 1rem;
            font-weight: 700;
            margin: 0;
          }

          p {
            text-align: left;
            font-size: 0.875rem;
            font-weight: 400;
            margin: 0;
          }

          .delete-line {
            width: 1.5rem;
            height: 1.5rem;
            margin-left: auto;
          }
        }

        .product-details {
          max-height: 0;
          overflow: hidden;
          transition:
            max-height 0.5s ease,
            padding 0.5s ease;
        }

        &.expanded {
          .product-details {
            margin: auto;
            padding: 0.625rem 0;
            overflow: visible;
            max-height: fit-content;

            .form-container {
              display: flex;
              align-items: flex-start;
              gap: 3%;

              .col {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 0.75rem;
                width: 100%;

                > * {
                  width: 100%;
                }
              }

              select {
                padding: 0 2.1875rem 0 0;
                background-size: 1.0625rem;
              }

              .has-tooltip {
                position: relative;
                cursor: pointer;

                &:hover {
                  .tooltip {
                    display: block;
                  }
                }

                .tooltip {
                  position: absolute;
                  padding: 0;
                  z-index: 9;
                  top: 50%;
                  left: 100%;
                  display: none;
                  width: 26rem;
                  height: 26rem;
                  transform: translateY(-10%);
                  box-shadow: 0 1.25rem 2.5rem -0.875rem rgba(0, 0, 0, 0.25);
                  background-position: center;
                  background-size: contain;
                  background-repeat: no-repeat;
                  background-color: var(--white-color);
                }
              }

              .images-group {
                width: 100%;
                text-align: left;

                .images-label {
                  font-size: 0.875rem;
                  font-weight: 400;
                }

                .image-upload-container {
                  display: flex;
                  flex-wrap: wrap;
                  gap: 0.5rem;
                  margin-top: 0.75rem;

                  span {
                    display: flex;
                    flex-direction: column;
                    width: 100%;

                    .large-images-row {
                      display: flex;
                      flex-wrap: wrap;
                      gap: 1rem;
                      margin-bottom: 1rem;
                      justify-content: flex-start;
                      align-items: flex-start;
                    }

                    .small-images-row {
                      display: flex;
                      flex-wrap: wrap;
                      gap: 0.75rem;
                      justify-content: flex-start;
                    }
                  }

                  @media (max-width: 768px) {
                    gap: 0.75rem;

                    span {
                      .large-images-row,
                      .small-images-row {
                        flex-direction: column;
                        gap: 0.75rem;
                      }
                    }
                  }

                  @media (max-width: 600px) {
                    /* phones */
                    span {
                      grid-template-columns: 1fr;
                    }
                    :deep(.case-line-image-input.lg),
                    :deep(.case-line-image-input.sm) {
                      grid-row: auto;
                      grid-column: auto;
                    }
                  }
                }
              }

              .combobox-cnt {
                padding: 0;
              }
            }
          }
        }

        &:last-child {
          border-radius: 0 0 0.25rem 0.25rem;
        }
      }
    }
  }

  .mandatory-fields {
    color: var(--primary-text-remark-color);
    border: none;
    font-size: small;
    text-align: left;
    background-color: transparent;
  }
}

@include breakpoint(medium) {
  .view-case {
    .form-container {
      flex-wrap: wrap;

      .col {
        margin-bottom: 0.75rem;
      }
    }

    .case-container .products-list .product .product-overview {
      div.product-info {
        width: 90%;
      }

      .defect-reason,
      .status,
      .customer-name {
        display: none;
      }
    }
  }
}
</style>

<style lang="scss">
.view-case {
  .form-container {
    input {
      &:disabled {
        cursor: initial;
      }
    }

    select {
      width: 100%;

      &:disabled {
        cursor: initial;
        color: var(--field-text-disabled-color);
      }
    }

    .comment-group span {
      width: 100%;

      p,
      textarea {
        width: 100%;
        resize: none;
        text-align: left;

        @include breakpoint(untillarge) {
          min-width: 100%;
        }
      }

      textarea {
        height: 1rem;

        &:disabled {
          cursor: initial;
        }
      }
    }

    select {
      &.option-placeholder {
        color: var(--primary-text-remark-color);
      }

      option {
        color: var(--primary-text-color);
      }
    }

    .field-new textarea {
      min-height: 89px;
      width: 100%;
      resize: vertical;
    }

    .label-request-input {
      margin: 0px 0px 10px 0px;
    }

    .label-request-textarea {
      height: 130px;
      max-height: 130px;
      margin: 0;
      resize: none;
    }
  }
}
</style>
